import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { X, ClipboardList, CheckCircle, XCircle, Lightbulb } from 'lucide-react';
import { ExerciseWithAttempts, ConceptCategory, getConceptLabelsForMode } from '@unity-learning/shared';
import { getExerciseHistory } from '../services/api';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';

// Fenêtre de la « session du jour » : tentatives des SESSION_WINDOW_HOURS dernières heures.
// Pour tester avec de vieilles données : augmenter temporairement (ex. 24 * 30).
const SESSION_WINDOW_HOURS = 12;

interface SessionRecapModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * B3 — Récap de fin de session (idée R30 enrichie par Maeva) : modale statique et
 * instantanée (AUCUN appel IA) construite depuis l'historique existant du mode courant,
 * filtré côté frontend sur les dernières SESSION_WINDOW_HOURS heures.
 * Sujets couverts + points à approfondir (concepts des exercices échoués du jour).
 */
const SessionRecapModal: React.FC<SessionRecapModalProps> = ({ isOpen, onClose }) => {
  const { user } = useUser();
  const { learningMode } = useLearningMode();
  const [sessionExercises, setSessionExercises] = useState<ExerciseWithAttempts[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;

    const fetchSession = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const history = await getExerciseHistory(user.id, { learningMode });
        if (cancelled) return;
        if (history) {
          const cutoff = Date.now() - SESSION_WINDOW_HOURS * 60 * 60 * 1000;
          setSessionExercises(
            history.items.filter(ex =>
              ex.userAttempt?.submittedAt && new Date(ex.userAttempt.submittedAt).getTime() >= cutoff
            )
          );
        } else {
          setSessionExercises([]);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSession();
    return () => { cancelled = true; };
  }, [isOpen, user, learningMode]);

  if (!isOpen) return null;

  const conceptLabels = getConceptLabelsForMode(learningMode);
  const labelOf = (concept: ConceptCategory): string => conceptLabels[concept] || concept;

  const succeeded = sessionExercises.filter(ex => ex.userAttempt?.isCorrect);
  const failed = sessionExercises.filter(ex => !ex.userAttempt?.isCorrect);
  const conceptsTouched = [...new Set(sessionExercises.flatMap(ex => ex.concepts))];
  const conceptsToReview = [...new Set(failed.flatMap(ex => ex.concepts))];

  const encouragement = sessionExercises.length === 0
    ? 'Même un seul exercice par jour fait la différence — à demain !'
    : conceptsToReview.length === 0
      ? 'Excellente session, tout est validé ! Revenez demain pour ancrer ces concepts dans la durée.'
      : 'Bonne session de travail. Les concepts à approfondir seront plus clairs à la prochaine tentative — c\'est exactement comme ça qu\'on progresse.';

  const markdownInline = {
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
    code: ({ children }: { children?: React.ReactNode }) => (
      <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
    ),
    p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>
  };

  // Portail obligatoire : le composant est monté dans <header>, qui porte un
  // `backdrop-blur-md`. Un backdrop-filter crée un bloc conteneur pour les
  // descendants en `position: fixed` — l'overlay se centrerait alors sur les
  // 64px du header et le haut de la modale sortirait de l'écran (cf. D1).
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ClipboardList className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Bilan de votre session
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Fermer"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {isLoading ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-6">Chargement du bilan...</div>
          ) : error ? (
            <div className="text-center text-red-600 dark:text-red-400 py-6">Impossible de charger le bilan : {error}</div>
          ) : sessionExercises.length === 0 ? (
            <div className="text-center text-gray-600 dark:text-gray-300 py-6">
              Aucun exercice terminé au cours des {SESSION_WINDOW_HOURS} dernières heures dans ce mode.
              <p className="mt-2 text-sm text-blue-900 dark:text-blue-200">
                Même un seul exercice par jour fait la différence — à demain !
              </p>
            </div>
          ) : (
            <>
              {/* Exercices de la session */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Exercices d'aujourd'hui — {succeeded.length} réussi{succeeded.length > 1 ? 's' : ''}, {failed.length} échoué{failed.length > 1 ? 's' : ''}
                </h3>
                <div className="space-y-2">
                  {sessionExercises.map(ex => (
                    <div
                      key={ex.id}
                      className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-100 dark:border-gray-700"
                    >
                      <div className="text-sm text-gray-900 dark:text-white min-w-0 truncate mr-3">
                        <ReactMarkdown components={markdownInline}>{ex.title}</ReactMarkdown>
                      </div>
                      {ex.userAttempt?.isCorrect ? (
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" aria-label="Réussi" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" aria-label="Échoué" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Concepts travaillés */}
              {conceptsTouched.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Concepts travaillés
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {conceptsTouched.map(concept => (
                      <span
                        key={concept}
                        className="px-2.5 py-1 rounded-full text-xs bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300"
                      >
                        {labelOf(concept)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Points à approfondir */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Points à approfondir
                </h3>
                {conceptsToReview.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {conceptsToReview.map(concept => (
                      <span
                        key={concept}
                        className="px-2.5 py-1 rounded-full text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300"
                      >
                        {labelOf(concept)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Rien à signaler : tout ce que vous avez tenté aujourd'hui est validé.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Mot d'encouragement statique — uniquement quand il y a des exercices à résumer
              (le cas vide affiche déjà son propre message d'encouragement ci-dessus) */}
          {!isLoading && !error && sessionExercises.length > 0 && (
            <div className="flex items-start p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Lightbulb className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-900 dark:text-blue-200">{encouragement}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            À bientôt !
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SessionRecapModal;
